import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Session from "../utils/sessionStorage";
import Avatar from "../components/Avatar";
import axiosInstance from "../utils/axiosInstance";
import nodata from "../assets/images/nodata.png";
import Cards from "../components/Cards";
import Prompt from "../components/Prompt";
import Confirmation from "../components/Confirmation";
import Button from "../components/form/Button";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import ConsentCheckModal from "../components/Home/ConcentCheckModal";
import { X_API_KEY } from "../constants/ServerUrl";
import SubscribePackageModal from "../components/SubscribePackageModal";
import ExtendValidityCarousel from "../components/ExtendValidityCarousel";
import { isAddPlanDisabled } from "../utils/profileutils";
import { ReactSVG } from "react-svg";
import dataicon from "../assets/svgs/dataicon.svg";
import callicon from "../assets/svgs/callicon.svg";
import MNPCard from "../components/Home/MNPCard";

let validityPackagesCache = null;
let validityPackagesRequest = null;
const dataPackagesCache = new Map();
const dataPackagesRequests = new Map();

const getUserDataFromSession = () => {
  const result = Session.get("_user_");

  if (!result) return "";

  try {
    return JSON.parse(result);
  } catch (error) {
    console.error("Unable to parse user session data:", error);
    return "";
  }
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is coming from auth page or if consent was already verified
  const isFromAuth = location.state?.fromAuth || false;
  const lastConsentCheck = sessionStorage.getItem('lastConsentCheck');
  const consentVerified = sessionStorage.getItem('consentVerified') === 'true';

  const [consentChecked, setConsentChecked] = useState(
    consentVerified && !isFromAuth ? true : false
  );

  const [userData, setUserData] = useState(() => getUserDataFromSession());

  const saOrgName = userData?.saOrgName;

  const [subsData, setSubsData] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [offerCode, setOfferCode] = useState("");
  const [isModalOpen, setIsOpenModal] = useState(false);
  const [isPromptModalShows, setIsPromptModalShows] = useState(false);
  const [accountData, setAccountData] = useState({
    balance: "",
    expireDate: "",
  });
  const [accountLoading, setAccountLoading] = useState(true);
  const [subsLoading, setSubsLoading] = useState(true);
  const [isXoxAccModalShow, setIsXoxAccModalShows] = useState(false);
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const [validityPackages, setValidityPackages] = useState(
    () => validityPackagesCache ?? []
  );
  const [validityPackagesLoading, setValidityPackagesLoading] = useState(false);
  const [dataPackages, setDataPackages] = useState(
    () => dataPackagesCache.get("/api/v2/get-data-packages") ?? []
  );
  const [dataPackagesLoading, setDataPackagesLoading] = useState(false);
  const [isValidityModalOpen, setIsValidityModalOpen] = useState(false);
  const [isDataPackageModalOpen, setIsDataPackageModalOpen] = useState(false);
  const [selectedValidityPackage, setSelectedValidityPackage] = useState(null);
  const [selectedValidityPrice, setSelectedValidityPrice] = useState(null);
  const [selectedDataPackage, setSelectedDataPackage] = useState(null);
  const [selectedDataPackagePrice, setSelectedDataPackagePrice] = useState(null);
  const [validitySubscriptionLoading, setValiditySubscriptionLoading] = useState(false);
  const [dataPackageSubscriptionLoading, setDataPackageSubscriptionLoading] = useState(false);
  const [disableAddPlan, setDisableAddPlan] = useState(null);

  const handleMobileNoChange = (updatedUserData) => {
    setUserData(updatedUserData);
    setSubsData([]);
    setErrMsg("");
    setAccountData({
      balance: "",
      expireDate: "",
    });
    setAccountLoading(true);
    setSubsLoading(true);
    setDisableAddPlan(null);
    setIsPromptModalShows(false);
    setIsXoxAccModalShows(false);
  };

  const openValidityModal = (packagename, price) => {
    setSelectedValidityPackage(packagename);
    setSelectedValidityPrice(price);
    setIsValidityModalOpen(true);
  };

  const openDataPackageModal = (packagename, price) => {
    setSelectedDataPackage(packagename);
    setSelectedDataPackagePrice(price);
    setIsDataPackageModalOpen(true);
  };

  const closeValidityModal = () => {
    setIsValidityModalOpen(false);
    setSelectedValidityPackage(null);
    setSelectedValidityPrice(null);
  };

  const closeDataPackageModal = () => {
    setIsDataPackageModalOpen(false);
    setSelectedDataPackage(null);
    setSelectedDataPackagePrice(null);
  };

  useEffect(() => {
    const handlePageShow = (event) => {
      // event.persisted is true when the page was restored from bfcache
      setIsDataPackageModalOpen(false);
      setSelectedDataPackage(null);
      setSelectedDataPackagePrice(null);

      // optional: also close validity modal / confirm modal if they can be affected
      setIsValidityModalOpen(false);
      setSelectedValidityPackage(null);
      setSelectedValidityPrice(null);
    };

    window.addEventListener("pageshow", handlePageShow);

    // fallback for browsers/webviews that don't fire pageshow reliably
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setIsDataPackageModalOpen(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const handleValiditySubscribe = async () => {
    const token = Session.get("token");
    const userMob = userData.mobileNo;
    const body = {
      msisdn: userMob,
      package: selectedValidityPackage,
    };

    try {
      setValiditySubscriptionLoading(true);
      const response = await axiosInstance.post(
        "api/v2/buy-data-validity",
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-API-KEY": X_API_KEY,
          },
        }
      );
      if (response.status === 200) {
        setValiditySubscriptionLoading(false);
        // closeValidityModal();
        await getAccount();
        return { success: true, message: "Subscribed Successfully" };
      }
    } catch (error) {
      setValiditySubscriptionLoading(false);
      throw error;
    }
  };

  const handleDataPackageSubscribe = async () => {
    const token = Session.get("token");
    const userMob = userData.mobileNo;
    const userWalletId = userData.walletId;
    const body = {
      msisdn: userMob,
      package: selectedDataPackage,
      amount: (Number(selectedDataPackagePrice)).toString(),
      wallet_id: userWalletId,
    };

    try {
      setDataPackageSubscriptionLoading(true);
      const url = disableAddPlan
        ? "api/v1/subscribe-package"
        : "api/v2/subscribe-package";

      const response = await axiosInstance.post(
        url,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-API-KEY": X_API_KEY,
          },
        }
      );

      if (response.status === 200) {
        setDataPackageSubscriptionLoading(false);
        // closeDataPackageModal();
        await getAccount();
        await getSubsPlan();
        return { success: true, message: "Subscribed Successfully" };
      }
    } catch (error) {
      setDataPackageSubscriptionLoading(false);
      throw error;
    }
  };

  const handleDirectDataPackageSubscribe = () => {
    const amountVal = Math.round(Number(selectedDataPackagePrice) * 100);
    const sAmt = amountVal;
    const sProdSubCode = String(amountVal).padStart(8, "0");
    const sBeneAcctNo = userData?.mobileNo;

    if (!selectedDataPackagePrice || !sBeneAcctNo || !saOrgName) {
      throw new Error("Unable to start direct subscription. Please try again.");
    }

    const url = `https://uat.onepay.com.my/dl/${saOrgName}/?OpName=VAS&ProdCode=09D&ProdSubCode=${sProdSubCode}&Amt=${sAmt}&BeneAcctNo=${sBeneAcctNo}`;
    window.location.href = url;

  };

  async function getValidityPackages() {
    if (validityPackagesCache !== null) {
      setValidityPackages(validityPackagesCache);
      return;
    }

    setValidityPackagesLoading(true);
    try {
      if (!validityPackagesRequest) {
        validityPackagesRequest = axiosInstance
          .get("/api/v2/get-validity-packages", {
            headers: {
              'X-API-KEY': X_API_KEY,
            },
          })
          .then((response) => {
            const { validity_packages } = response?.data;
            const packages = Array.isArray(validity_packages)
              ? validity_packages
              : [];

            validityPackagesCache = packages;
            return packages;
          })
          .finally(() => {
            validityPackagesRequest = null;
          });
      }

      const packages = await validityPackagesRequest;
      setValidityPackages(packages);
    } catch (error) {
      console.error("Error fetching validity packages:", error);
    } finally {
      setValidityPackagesLoading(false);
    }
  }

  async function getDataPackages() {
    const url = disableAddPlan
      ? "/api/v1/get-data-packages"
      : "/api/v2/get-data-packages";

    if (dataPackagesCache.has(url)) {
      setDataPackages(dataPackagesCache.get(url));
      return;
    }

    setDataPackagesLoading(true);
    try {
      let request = dataPackagesRequests.get(url);

      if (!request) {
        request = axiosInstance
          .get(url, {
            headers: {
              'X-API-KEY': X_API_KEY,
            },
          })
          .then((response) => {
            const { data_packages } = response?.data;
            const packages = Array.isArray(data_packages) ? data_packages : [];

            dataPackagesCache.set(url, packages);
            return packages;
          })
          .finally(() => {
            dataPackagesRequests.delete(url);
          });

        dataPackagesRequests.set(url, request);
      }

      const packages = await request;
      setDataPackages(packages);
    } catch (error) {
      console.error("Error fetching data packages:", error);
      setDataPackages([]);
    } finally {
      setDataPackagesLoading(false);
    }
  }

  useEffect(() => {
    if (consentChecked) {
      getValidityPackages();
      getDataPackages();
    }
  }, [consentChecked]);


  async function getSubsPlan() {
    setSubsLoading(true);
    try {
      const url = disableAddPlan
        ? "api/v1/subscription-info"
        : "api/v2/subscription-info";
      const response = await axiosInstance.get(url, {
        params: {
          msisdn: userData?.mobileNo,
          // offer_code: offerCode
        },
        headers: {
          'X-API-KEY': X_API_KEY,
        },
      });

      if (response.status === 200) {
        const subscriptions = response?.data?.subscriptions;
        setSubsData(Array.isArray(subscriptions) ? subscriptions.filter(Boolean) : []);
      }
    } catch (error) {
      setSubsData([]);
      // setSubsLoading(false);
      if (
        error?.response?.data?.message ===
        "getDataSubscription : Can't find subscription by given msisdn!"
      ) {
        setErrMsg("No Subscription Record found");
      }
      // toast.error(error?.response?.data?.message, {
      //   position: "top-center",
      //   autoClose: 5000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      //   progress: undefined,
      //   theme: "light",
      // });
    }
    finally {
      setSubsLoading(false);
    }
  }

  async function getProfileData() {
    try {
      const response = await axiosInstance.get("api/v1/get-profile", {
        params: {
          msisdn: userData?.mobileNo,
        },
        headers: {
          'X-API-KEY': X_API_KEY,
        },
      });
      if (response.status === 200) {
        if (userData?.customerType === "StdCDD") {
          const profile = response?.data?.profile;
          setOfferCode(profile?.offerCode)

          Session.set("profile", JSON.stringify(profile));
          if (profile) {
            setIsXoxAccModalShows(false);
          } else {
            setIsXoxAccModalShows(true);
          }
        } else {
          const profile = response?.data?.profile;
          Session.set("profile", JSON.stringify(profile));
        }
        setDisableAddPlan(isAddPlanDisabled());
        getAccount();
      }

    } catch (error) {
      console.log("error tftyft", error);

      if (
        error?.response?.data?.message === "getPrepaidProfile : Can't find subscription by given msisdn! "
      ) {
        console.log('drdrd')
        setIsPromptModalShows(true);
      }
    }
  }

  async function getAccount() {
    try {
      setAccountLoading(true);
      const response = await axiosInstance.get("api/v1/get-account", {
        params: {
          msisdn: userData?.mobileNo,
        },
        headers: {
          'X-API-KEY': X_API_KEY,
        },
      });
      if (response.status === 200) {
        const account = response?.data?.account;
        setAccountLoading(false);
        setAccountData((prev) => ({
          ...prev,
          balance: account?.totalBal,
          expireDate: account?.expiryDate,
        }));
        // getSubsPlan();
      }
    } catch (error) {
      setAccountLoading(false);
    } finally {
      setAccountLoading(false)
    }
  }

  const checkConsent = async () => {
    try {
      const walletUserSessToken = Session.get("walletUserSess");
      const consentToken = Session.get("_consent_Tkn");

      if (!walletUserSessToken) {
        window.location.href = `https://uat.onepay.com.my/dl/${saOrgName}/?OpName=Close`;
        return;
      }

      const body = {
        walletUserSessToken: walletUserSessToken,
        ...(consentToken && { consentToken: consentToken }),
      };

      const response = await axiosInstance.post("api/v1/get-user-info", body);
      const { userInfo } = response.data;

      if (userInfo) {
        const { consentStatus, customerType, consentToken: newConsentToken } = userInfo;

        // Update the session with the latest status and token
        Session.set("status", consentStatus);
        if (newConsentToken) {
          Session.set("_consent_Tkn", newConsentToken);
        }
        if (customerType !== "StdCDD") {
          // Mark consent as verified and store timestamp
          sessionStorage.setItem('consentVerified', 'true');
          sessionStorage.setItem('lastConsentCheck', Date.now().toString());
          return;
        }

        if (consentStatus !== "APPR") {
          window.location.href = `https://uat.onepay.com.my/dl/${saOrgName}/?OpName=Close`;
          return;
        }

        // Mark consent as verified and store timestamp
        sessionStorage.setItem('consentVerified', 'true');
        sessionStorage.setItem('lastConsentCheck', Date.now().toString());
      } else {
        window.location.href = `https://uat.onepay.com.my/dl/${saOrgName}/?OpName=Close`;
      }
    } catch (error) {
      console.error("Consent check failed:", error);
      // Clear verification status on error
      sessionStorage.removeItem('consentVerified');
      sessionStorage.removeItem('lastConsentCheck');
      window.location.href = `https://uat.onepay.com.my/dl/${saOrgName}/?OpName=Close`;
    } finally {
      setConsentChecked(true);
    }
  };

  // useEffect(() => {
  //   if (offerCode) {
  //     getSubsPlan();
  //   }
  // }, [offerCode]);

  useEffect(() => {
    const shouldCheckConsent = () => {
      // Always check if coming from auth
      if (isFromAuth) return true;

      // Check if consent was verified recently (within last 30 minutes)
      const lastCheck = sessionStorage.getItem('lastConsentCheck');
      if (!lastCheck) return true;

      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
      const isStale = (Date.now() - parseInt(lastCheck)) > thirtyMinutes;

      // Check if verification status exists and is not stale
      const isVerified = sessionStorage.getItem('consentVerified') === 'true';

      return !isVerified || isStale;
    };

    if (shouldCheckConsent()) {
      // Clear the fromAuth state after using it
      if (location.state?.fromAuth) {
        // Replace the current history entry to remove the state
        navigate(location.pathname, { replace: true });
      }

      checkConsent();
    } else {
      // Skip consent check, user was already verified recently
      setConsentChecked(true);
    }
  }, [isFromAuth, location, navigate]);

  useEffect(() => {
    // Only fetch data if consent is checked
    if (consentChecked && userData?.mobileNo) {
      getProfileData();
    }
  }, [consentChecked, userData?.mobileNo]);

  useEffect(() => {
    if (disableAddPlan !== null && userData?.mobileNo) {
      getSubsPlan();
    }
  }, [disableAddPlan, userData?.mobileNo]);

  // useEffect(() => {
  //   setDisableAddPlan(isAddPlanDisabled());
  // }, []);

  // useEffect(()=>{
  //   getSubsPlan();
  // })

  const handleSubscribe = () => {
    const sProdCode = "09B";
    const sProdSubCode = "00002500";
    let amount = 25 * 100;
    const sAmt = amount;
    const sBeneAcctNo = userData?.mobileNo;

    const url = `app://mobilityone.mipay.vas/?ProdCode=09B&ProdSubCode=00002500&Amt=${sAmt}&BeneAcctNo=${sBeneAcctNo}`;
    // console.log("old url: " , url)
    window.location.href = url;
  };

  function calculateDaysDifference(expirationDate) {
    const currentDate = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" })
    );
    const expiryDate = new Date(expirationDate);
    const differenceInTime = expiryDate - currentDate; // Difference in milliseconds
    const differenceInDays = Math.ceil(
      differenceInTime / (1000 * 60 * 60 * 24)
    ); // Convert to days
    return differenceInDays > 0 ? differenceInDays : 0; // Return 0 if expiration is in the past
  }

  const formatRemainingQuota = (remainingQuota) => {
    const cleanedQuota = extractQuotaValue(remainingQuota); // Use the extracted value
    return cleanedQuota;
  };

  const extractQuotaValue = (remainingQuota) => {
    if (!remainingQuota) return 0; // Fallback for missing data
    const cleanedQuota = parseFloat(String(remainingQuota).split(" ")[0]); // Extract numeric part as a float
    return isNaN(cleanedQuota) ? 0 : cleanedQuota; // Fallback for invalid numbers
  };

  const handleConfirmation = async () => {
    setIsConfirmModal(true);
  };

  const handleMore = () => {
    navigate("/more-detail")
  };

  if (!consentChecked) {
    return <ConsentCheckModal />;
  }
  return (
    <div className="flex flex-col max-mobile_lg:w-full items-center  h-screen mx-auto">
      <div className="max-mobile_lg:w-full bg-accent-grey-50 pb-5">
        <div>
          <Avatar userData={userData} onMobileNoChange={handleMobileNoChange} />
          {accountLoading ? (
            <div className="flex items-center justify-around w-full animate-pulse bg-white h-[110px] mt-4 rounded-3xl shadow-md">
              {/* Expiry Date Placeholder */}
              <div className="flex flex-col gap-1 items-center">
                <div className="h-3 w-[50px] bg-slate-300 rounded-md"></div>
              </div>

              {/* Prepaid Airtime Placeholder */}
              <div className="flex flex-col gap-1 items-center">
                <div className="h-3 w-[70px] bg-slate-300 rounded-md"></div>
              </div>
            </div>
          ) : (

            <div className="flex items-center justify-between mx-[16px] px-6 bg-primary h-[110px] mt-4 rounded-3xl">
              {accountData?.balance ? (
                <div className="flex flex-col justify-between">
                  <p className="text-white text-[13px] font-normal">
                    Prepaid Airtime
                  </p>
                  <p className="text-white text-[17px] font-semibold">
                    RM {accountData?.balance || 0}
                  </p>

                  <p className="text-white text-[13px] font-semibold">
                    <span className="opacity-[48%] font-semibold">Expiry </span>
                    {accountData?.expireDate || 0}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col justify-between">
                  <p className="text-white text-[13px] font-normal">
                    Prepaid Airtime
                  </p>
                  <p className="text-white text-[17px] font-semibold">
                    RM 0.00
                  </p>

                  <p className="text-white text-[13px] font-semibold">
                    <span className="opacity-[48%] font-semibold">Expiry </span>
                    00-00-0000 00:00:00
                  </p>
                </div>
              )}
              <button
                onClick={() => navigate("/topup")}
                className="bg-white text-primary text-[13px] py-[6px] px-6 rounded-full"
              >
                Topup
              </button>

            </div>
          )}
        </div>
        {/* percentage */}

        <div className="mt-8 bg-primary/5 rounded-3xl px-[15px] py-8 mb-6">
          {subsLoading ? (
            <>
              <div className="animate-pulse flex flex-col justify-center  bg-white mt-[33px] h-20 rounded-lg mb-3 space-y-3 p-3">
                <div className="h-3 w-1/2 bg-slate-300 rounded-md"></div>
                <div className="h-3 w-full bg-slate-300 rounded-md"></div>
              </div>
            </>

          ) : (
            <>
              {subsData?.length > 0 &&
                <div className="flex justify-between">
                  <p className="font-semibold text-[17px]">
                    Remaining Usage
                  </p>
                  {subsData?.length > 0 && (
                    <>
                      {subsData[0].tier !== "LBASIC64" && (
                        <p className="text-primary text-[13px] font-semibold">
                          {subsData[0].tier || ""}
                        </p>
                      )}
                    </>
                  )}
                </div>
              }
              {subsData?.length === 0 ? (

                <div className="flex flex-col justify-center  mt-[33px] h-44 rounded-lg mb-3">
                  <div className="mx-auto">
                    <img src={nodata} alt="no data" className="w-full max-w-[211px] h-full" />
                  </div>
                  <p className="font-poppins text-base font-medium text-accent-silver-900 text-center">
                    No Plan Subscribed
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4 items-center mt-4">
                  {subsData?.map((item, index) => {
                    const {
                      expiration,
                      volume,
                      remainingPercentage,
                      remainingQuota,
                      remaining,
                      tier,
                      validity,
                      capped_speed,
                      capped_volume, //total
                      capped,          //remaining
                      hotspot_speed,
                      hotspot_volume,
                      hotspot,
                      uncapped,
                      uncapped_volume,
                      uncapped_speed,
                      free_call_type,
                      free_call,
                      free_call_volume
                    } = item;

                    const daysRemaining = expiration
                      ? calculateDaysDifference(expiration)
                      : "N/A";

                    const remi = (daysRemaining * 100) / 30;
                    const remaining_capped_GB = capped / 1024;
                    const total_capped_volume_GB = capped_volume / 1024;
                    const total_uncapped_volume_GB = uncapped_volume / 1024;
                    const total_hotspot_volume_GB = hotspot_volume / 1024;
                    const remaining_hotspot_GB = hotspot / 1024;
                    const remaining_uncapped_GB = uncapped / 1024;
                    const formattedQuota = formatRemainingQuota(remainingQuota);
                    const volumeValue = Number(volume) || 0;
                    const remainingValue = Number(remaining) || 0;
                    const capped_used = total_capped_volume_GB - remaining_capped_GB;
                    const uncapped_used = total_uncapped_volume_GB - remaining_uncapped_GB;
                    const hotspot_used = total_hotspot_volume_GB - remaining_hotspot_GB;
                    const used_call_minutes = free_call_volume - free_call;

                    return (
                      <div key={index} className="flex flex-col gap-3 items-center  w-full">
                        <div className=" pb-2 w-full">
                          <div className="grid grid-cols-2 gap-4">
                            {disableAddPlan ? (
                              <>
                                {/* <div className="w-full flex-col items-center justify-center">
                                <CircularProgressbar
                                  value={
                                    remainingPercentage ? remainingPercentage : 95
                                  }
                                  text={
                                    tier === "UNL35" || tier === "FW25"
                                      ? <>
                                        Unlimited
                                        <tspan x="50%" dy="1em">GB</tspan>
                                      </>
                                      : `${formattedQuota}`
                                  }
                                  styles={buildStyles({
                                    pathColor: "red",
                                    textColor: "black",
                                    textSize: "17.5px",
                                  })}
                                />
                                <div className=" flex flex-col justify-center items-center  ">
                                  <p className=" pt-2 text-black text-sm opacity-[60%]">
                                    {tier === "UNL35" || tier === "FW25"
                                      ? "Unlimited GB"
                                      : volume
                                        ? volume
                                        : "N/A"}
                                  </p>
                                  <h3 className=" text-primary">
                                    {volume ? "H/S Internet" : "Basic Internet"}{" "}
                                  </h3>
                                </div>
                              </div> */}
                                <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3 col-span-2">
                                  <div className="flex items-center gap-[10px]">
                                    <ReactSVG src={dataicon} />
                                    <div className="font-semibold text-[17px] flex items-center gap-[2px]">

                                      {tier === "UNL35" || tier === "FW25" ? (
                                        <span className="text-[13px] text-gray"> Unlimited</span>

                                      ) : (
                                        <>
                                          Data {formattedQuota.toFixed(2)} {" "}
                                          <span className="text-[13px] text-gray font-normal"> GB</span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  <div className="mt-[21px]">
                                    {volumeValue < 90 && (
                                      <p className="text-[13px]">
                                        <span className="text-gray">Used: </span>
                                        {(volumeValue - remainingValue).toFixed(2)} GB out of {volumeValue.toFixed(2)} GB
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                    <div
                                      className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                      style={{
                                        width: volumeValue
                                          ? `${(remainingValue / volumeValue) * 100}%`
                                          : "100%",
                                      }}
                                    ></div>
                                  </div>

                                  <div className="flex justify-between mt-2 text-gray text-[13px] pb-2">
                                    <p>{volumeValue ? "H/S Internet" : "Basic Internet"}</p>
                                    <p>
                                      Remaining:{" "}
                                      <span className="text-black font-semibold">
                                        {volumeValue > 90 ? "Unlimited" : `${remainingValue.toFixed(2)} GB`}
                                      </span>
                                    </p>
                                  </div>
                                </div>


                                {/* <div className="w-full flex-col items-center justify-center">
                                <CircularProgressbar
                                  value={remi}
                                  text={`${daysRemaining} days`}
                                  styles={buildStyles({
                                    pathColor: "red",
                                    textColor: "black",
                                    textSize: "18px",
                                  })}
                                />
                                <div className=" flex flex-col justify-center items-center ">
                                  <p className=" pt-2 text-black text-sm opacity-[60%]">
                                    {validity ? validity : "N/A"}
                                  </p>
                                  <h3 className=" text-primary text-sm">Validity</h3>
                                </div>
                              </div> */}

                                {/* <div className="w-full flex-col items-center justify-center">
                                <CircularProgressbar
                                  value={0}
                                  text="0.0"
                                  styles={buildStyles({
                                    pathColor: "red",
                                    textColor: "black",
                                    textSize: "18px",
                                  })}
                                />
                                <div className=" flex flex-col justify-center items-center  ">
                                  <p className=" pt-2 text-black text-sm opacity-[60%]">
                                    0.0
                                  </p>
                                  <h3 className=" text-primary">Voice Call</h3>
                                </div>
                              </div> */}

                                {/* <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3">
                                <div className="flex items-center gap-[10px]">
                                  <ReactSVG src={callicon} />
                                  <div className="font-semibold text-[17px]">
                                    Call
                                  </div>
                                </div>



                                <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                  <div
                                    className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                    style={{ width: "0%" }}
                                  ></div>
                                </div>

                                <div className="flex justify-between mt-2 text-gray text-[13px] pb-2">
                                  N/A
                                  <p>
                                    <span className="text-black font-semibold">0 Mins</span>
                                  </p>
                                </div>
                              </div> */}

                                {/* <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3">
                                <div className="flex items-center gap-[10px]">
                                  <ReactSVG src={dataicon} />
                                  <div className="font-semibold text-[17px]">
                                    Hotspot
                                  </div>
                                </div>

                                <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                  <div
                                    className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                    style={{ width: "0%" }}
                                  ></div>
                                </div>

                                <div className="mt-2 text-gray text-[13px] pb-2">
                                  <p className="flex justify-between">
                                    N/A
                                    <span className="text-black font-semibold">0 GB</span>
                                  </p>
                                </div>
                              </div> */}


                              </>
                            ) : (
                              <>
                                {/* <div className="w-full flex flex-col justify-center h-[159px] bg-white rounded-3xl p-3">
                                <div className="w-full text-center text-black mb-2">
                                  {`${daysRemaining} days`}
                                </div>

                                <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden">
                                  <div
                                    className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                    style={{ width: `${remi}%` }}
                                  ></div>
                                </div>

                                <div className="flex flex-col justify-center items-center mt-2">
                                  <p className="pt-2 text-black text-sm opacity-[60%]">
                                    {validity ? validity : "N/A"}
                                  </p>
                                  <h3 className="text-primary text-sm">Validity</h3>
                                </div>
                              </div> */}


                                {
                                  (capped > 0 || capped_volume > 0) ? (
                                    <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3 col-span-2">
                                      <div className="flex items-center gap-[10px]">
                                        <ReactSVG src={dataicon} />
                                        <div className="font-semibold text-[17px] flex items-center gap-[2px]">
                                          {capped ? (
                                            total_capped_volume_GB > 40 ? (
                                              <div> Data <span className="text-[13px] font-normal text-gray">Unlimited</span></div>
                                            ) : (
                                              <>
                                                Data {total_capped_volume_GB.toFixed(2)} <span className="text-[13px] font-normal text-gray">GB</span>
                                              </>
                                            )
                                          ) : (
                                            "N/A"
                                          )}
                                        </div>
                                      </div>

                                      <div className="mt-[21px]">
                                        {total_capped_volume_GB < 40 && (
                                          <p className="text-[13px]">
                                            <span className="text-gray">Used: </span>
                                            {capped_used.toFixed(2)} GB out of {total_capped_volume_GB.toFixed(2)} GB
                                          </p>
                                        )}
                                      </div>

                                      <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                        <div
                                          className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                          style={{
                                            width: capped_volume
                                              ? `${(parseFloat(capped) / parseFloat(capped_volume)) * 100}%`
                                              : "100%",
                                          }}
                                        ></div>
                                      </div>

                                      <div className="flex justify-between mt-2 text-gray text-[13px] pb-2">
                                        <p className="">
                                          {capped_speed ? capped_speed + " Data" : "N/A"}

                                        </p>
                                        <p>
                                          Remaining: <span className="text-black font-semibold">{remaining_capped_GB > 40 ? "unlimited" : <> {remaining_capped_GB.toFixed(2)} GB</>}</span>
                                        </p>
                                      </div>
                                    </div>

                                  ) : (
                                    <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3 col-span-2">
                                      <div className="flex items-center gap-[10px]">
                                        <ReactSVG src={dataicon} />
                                        <div className="font-semibold text-[17px] flex items-center gap-[2px]">
                                          {uncapped ? (
                                            total_uncapped_volume_GB > 90 ? (
                                              <span className="text-[13px] text-gray"> Unlimited</span>
                                            ) : (
                                              <>
                                                Data {total_uncapped_volume_GB.toFixed(2)} {" "}
                                                <span className="text-[13px] text-gray font-normal"> GB</span>
                                              </>
                                            )
                                          ) : (
                                            "N/A"
                                          )}
                                        </div>
                                      </div>

                                      <div className="mt-[21px]">
                                        {total_uncapped_volume_GB < 90 && (
                                          <p className="text-[13px]">
                                            <span className="text-gray">Used: </span>
                                            {uncapped_used.toFixed(2)} GB out of {total_uncapped_volume_GB.toFixed(2)} GB
                                          </p>
                                        )}
                                      </div>

                                      <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                        <div
                                          className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                          style={{
                                            width: uncapped_volume
                                              ? `${(parseFloat(uncapped) / parseFloat(uncapped_volume)) * 100}%`
                                              : "100%",
                                          }}
                                        ></div>
                                      </div>

                                      <div className="flex justify-between mt-2 text-gray text-[13px] pb-2">
                                        <p>{uncapped_speed ? `${uncapped_speed} H/S Data` : "N/A"}</p>
                                        <p>
                                          Remaining:{" "}
                                          <span className="text-black font-semibold">
                                            {remaining_uncapped_GB > 90 ? "Unlimited" : `${remaining_uncapped_GB.toFixed(2)} GB`}
                                          </span>
                                        </p>
                                      </div>
                                    </div>

                                  )
                                }
                                {/* {(hotspot > 0 || hotspot_volume > 0) && (
                                <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3">
                                  <div className="flex items-center gap-[10px]">
                                    <ReactSVG src={dataicon} />
                                    <div className="font-semibold text-[17px]">
                                      {hotspot_volume ? (
                                        total_hotspot_volume_GB > 0 ? (
                                          <>
                                            Hotspot{" "}
                                            <span className="whitespace-nowrap">
                                              {total_hotspot_volume_GB}{" "}
                                              <span className="text-[13px] text-gray">GB</span>
                                            </span>
                                          </>

                                        ) : (
                                          "unlimited"
                                        )
                                      ) : (
                                        "N/A"
                                      )}
                                    </div>
                                  </div>

                                  <div className="mt-[21px]">
                                    {total_hotspot_volume_GB > 0 && (
                                      <p className="text-[13px]">
                                        <span className="text-gray">Used: </span>
                                        {hotspot_used} GB out of {total_hotspot_volume_GB} GB
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                    <div
                                      className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                      style={{
                                        width: hotspot_volume
                                          ? `${(parseFloat(hotspot) / parseFloat(hotspot_volume)) * 100}%`
                                          : "100%",
                                      }}
                                    ></div>
                                  </div>

                                  <div className="mt-2 text-gray text-[13px] pb-2">
                                    <p className="flex justify-between" >
                                      Remaining:{" "}
                                      <span className="text-black font-semibold">
                                        {remaining_hotspot_GB > 90 ? "unlimited" : `${remaining_hotspot_GB} GB`}
                                      </span>
                                    </p>
                                  </div>
                                </div>

                              )} */}
                                {/* <div className="w-full flex flex-col justify-center bg-white rounded-3xl p-3">
                                <div className="flex items-center gap-[10px]">
                                  <ReactSVG src={callicon} />
                                  <div className="font-semibold text-[17px]">
                                    {free_call_volume ? (
                                      free_call_volume === "Unlimited" ? (
                                        <>
                                          Call {" "}<span className="text-[13px] font-normal text-gray">Unlimited</span>
                                        </>
                                      ) : (
                                        <>
                                          Call {free_call_volume}{" "}
                                          <span className="text-[13px] text-gray">Mins</span>
                                        </>
                                      )
                                    ) : (
                                      "N/A"
                                    )}
                                  </div>
                                </div>

                                <div className="mt-[21px]">
                                  {free_call_volume !== "Unlimited" && (
                                    <p className="text-[13px]">
                                      <span className="text-gray">Used: </span>
                                      {used_call_minutes ? used_call_minutes : "N/A"}{" "} Mins
                                    </p>
                                  )}
                                </div>

                                <div className="w-full bg-primary/10 rounded-full h-[15px] overflow-hidden mt-[11px]">
                                  <div
                                    className="h-[15px] bg-gradient-to-r from-gradiantprimary to-gradiantsecondary transition-all duration-700 rounded-full"
                                    style={{
                                      width:
                                        free_call_volume && free_call_volume !== "Unlimited"
                                          ? `${(parseFloat(used_call_minutes) / parseFloat(free_call_volume)) * 100}%`
                                          : "100%",
                                    }}
                                  ></div>
                                </div>

                                <div className="flex justify-between mt-2 text-gray text-[13px] pb-2">
                                  {free_call_type ? free_call_type : "N/A"}
                                  {":"}
                                  <p>
                                    <span className="text-black font-semibold">
                                      {free_call_volume === "Unlimited"
                                        ? " Unlimited"
                                        : `${free_call_volume && used_call_minutes
                                          ? free_call_volume - used_call_minutes
                                          : 0
                                        } Mins`}
                                    </span>
                                  </p>
                                </div>
                              </div> */}


                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className={`w-full grid ${!disableAddPlan ? "grid-cols-2" : "grid-cols-1"}  gap-3 justify-center`}>
                    {!disableAddPlan && (
                      <Button
                        classes={"text-primary border border-[2px] border-primary bg-white"}
                        children={"More"}
                        roundness={"pill"}
                        type={"button"}
                        size={"costum"}
                        eventAction={handleMore}
                        intent={"more"}
                      />
                    )}
                    <Button
                      children={"Unsubscribe"}
                      intent={"primary"}
                      roundness={"pill"}
                      type={"button"}
                      size={"costum"}
                      eventAction={handleConfirmation}
                    />
                  </div>
                </div>
              )}
            </>)}
          {!disableAddPlan && (
            <>
              <h1 className='text-[17px] font-semibold pt-8'>New Plan</h1>

              <ExtendValidityCarousel
                packages={dataPackages}
                onSubscribe={openDataPackageModal}
                loading={dataPackagesLoading}
                type="data"
              />
            </>
          )}
        </div>


        <div className="mt-8">
          <h1 className='text-[17px] font-semibold ml-4'>Quick Services</h1>
          <div className=" w-full bg-white rounded-lg min-h-30 px-4 py-4">
            <Cards />
          </div>
        </div>

        {!disableAddPlan && (
          <div className="mt-8">
            <h1 className='text-[17px] font-semibold ml-2'>Extend Validity</h1>
            <div className="max-w-[660px]">
              <div className="w-full bg-white rounded-3xl min-h-30 px-4 py-4">
                <ExtendValidityCarousel
                  packages={validityPackages}
                  onSubscribe={openValidityModal}
                  loading={validityPackagesLoading}
                />
              </div>
            </div>
          </div>
        )}





        <MNPCard />
        {isXoxAccModalShow && <Prompt call={"notXoxUser"} />}
        {isPromptModalShows && <Prompt call={"home"} />}
        {isConfirmModal && (
          <Confirmation close={() => {
            setIsConfirmModal(false);
            getSubsPlan();
          }} />
        )}
      </div>
      <SubscribePackageModal
        isOpen={isValidityModalOpen}
        onClose={closeValidityModal}
        onConfirm={handleValiditySubscribe}
        packagename={selectedValidityPackage}
        packageprice={selectedValidityPrice}
      />
      <SubscribePackageModal
        isOpen={isDataPackageModalOpen}
        onClose={closeDataPackageModal}
        onConfirm={handleDataPackageSubscribe}
        onSecondaryConfirm={handleDirectDataPackageSubscribe}
        packagename={selectedDataPackage}
        packageprice={selectedDataPackagePrice}
        secondaryLabel={"Direct Subscribe"}
        confirmLabel={"Airtime Subscribe"}
        buttonGridClasses="grid-cols-1"
        buttonsColorOrder="reverse"
      />
    </div >
  );
};

export default Home;
